import ArticleLayout from "@/components/feature/ArticleLayout";
import { Link } from "react-router-dom";

const tips = [
  "Sortiere nach Beliebtheit, um Trend-Produkte schnell zu identifizieren",
  "Vergleiche mindestens drei ähnliche Produkte vor einer Kaufentscheidung",
  "Achte auf die Verkäuferbewertung — mindestens 95% positive Bewertungen",
  "Fordere immer QC-Bilder an, bevor der Versand erfolgt",
  "Nutze die Größenangaben im Spreadsheet, um Retouren zu vermeiden",
];

const faqs = [
  { q: "Welche Zahlungsmethoden werden bei OOPBUY akzeptiert?", a: "OOPBUY akzeptiert verschiedene Zahlungsmethoden, darunter Kreditkarten, PayPal und Überweisungen. Die genauen Optionen findest du im OOPBUY Spreadsheet." },
  { q: "Wie lange dauert der Versand nach Deutschland?", a: "Die Versanddauer hängt von der gewählten Versandmethode ab. In der Regel beträgt die Lieferzeit zwischen 7 und 21 Tagen nach Deutschland." },
  { q: "Kann ich Produkte aus verschiedenen Kategorien in einer Bestellung kombinieren?", a: "Ja, das OOPBUY Spreadsheet ermöglicht es dir, Produkte aus allen 14 Kategorien in einer einzigen Bestellung zusammenzufassen." },
];

export default function EinkaufsguidePage() {
  return (
    <ArticleLayout
      title1="OOPBUY Spreadsheet"
      title2="Einkaufsguide 2026"
      title3="Schritt für Schritt erklärt"
      date="14. Juli 2026"
      readTime="14 Minuten"
      tag="Guide"
      seoDescription="Der ultimative OOPBUY Spreadsheet Einkaufsguide 2026. Lerne in 9 Schritten, wie du Produkte schneller findest, Preise vergleichst und sicher auf OOPBUY einkaufst — komplett auf Deutsch."
      faqs={faqs}
    >
      <p className="text-lg text-foreground-700 leading-relaxed">
        Willkommen zum ultimativen <strong>OOPBUY Spreadsheet Einkaufsguide</strong> für 2026. In diesem umfassenden Guide zeigen wir dir, wie du mit dem OOPBUY Spreadsheet Produkte schneller findest, bessere Kaufentscheidungen triffst und das Beste aus deinem Shopping-Erlebnis herausholst — komplett auf Deutsch erklärt und speziell für den deutschsprachigen Raum optimiert.
      </p>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Warum ein strukturierter Einkaufsguide wichtig ist
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        Ohne eine klare Strategie kann das Einkaufen über OOPBUY schnell überwältigend wirken. Das <strong>OOPBUY Spreadsheet</strong> bietet dir genau die Struktur, die du brauchst, um den Überblick zu behalten. Mit unserem Einkaufsguide lernst du, wie du das Spreadsheet als dein zentrales Tool nutzt — von der ersten Produktsuche bis zur finalen Bestellung.
      </p>

      <div className="my-6">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20white%20desk%20setup%20with%20fashion%20moodboard%20and%20product%20catalog%20spread%20out%2C%20natural%20morning%20light%20streaming%20through%20window%2C%20organized%20creative%20workspace%2C%20editorial%20photography%20style%2C%20sage%20green%20and%20warm%20beige%20color%20palette%2C%20clean%20aesthetic%2C%20soft%20shadows%2C%20professional%20composition&width=1200&height=700&seq=article-einkaufsguide-workspace-v2&orientation=landscape"
          alt="OOPBUY Spreadsheet Einkaufsguide Workspace"
          className="w-full rounded-xl object-cover"
        />
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Phase 1: Vorbereitung und Recherche
      </h2>
      <div className="space-y-4 my-6">
        {[
          { step: "1", title: "Kategorien erkunden", desc: "Öffne das OOPBUY Spreadsheet und verschaffe dir einen Überblick über alle 14 Produktkategorien. Notiere dir, welche Kategorien für dich relevant sind." },
          { step: "2", title: "Budget festlegen", desc: "Lege dein maximales Budget fest, bevor du mit dem Stöbern beginnst. Das OOPBUY Spreadsheet hilft dir, Produkte innerhalb deiner Preisklasse zu filtern." },
          { step: "3", title: "Prioritäten setzen", desc: "Entscheide, welche Produkte für dich Priorität haben. Das Spreadsheet ermöglicht es dir, Produkte nach Dringlichkeit zu sortieren." },
        ].map((step) => (
          <div key={step.step} className="bg-background-100 rounded-xl p-5 border border-foreground-100/30">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{step.step}</div>
              <h3 className="font-heading font-semibold text-foreground-900 text-base">{step.title}</h3>
            </div>
            <p className="text-sm text-foreground-600 ml-11">{step.desc}</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Phase 2: Produktauswahl mit dem OOPBUY Spreadsheet
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        Das <strong>OOPBUY Spreadsheet</strong> macht die Produktauswahl zum Kinderspiel. Statt dich durch unzählige Seiten zu klicken, siehst du alle relevanten Produkte auf einen Blick. Achte bei der Auswahl besonders auf Verkäuferbewertungen, Produktbeschreibungen und verfügbare QC-Bilder.
      </p>

      <div className="space-y-4 my-6">
        {[
          { step: "4", title: "Verkäufer bewerten", desc: "Prüfe die Verkäuferbewertung im Spreadsheet. Ein guter Verkäufer hat mindestens 95% positive Bewertungen und eine hohe Anzahl abgeschlossener Transaktionen." },
          { step: "5", title: "Produktdetails checken", desc: "Lies dir die Produktbeschreibungen genau durch. Achte auf Materialien, Größen und eventuelle Besonderheiten, die im OOPBUY Spreadsheet vermerkt sind." },
          { step: "6", title: "QC-Bilder anfordern", desc: "Bevor der Versand erfolgt, fordere Qualitätskontrollbilder an. So stellst du sicher, dass das Produkt deinen Erwartungen entspricht." },
        ].map((step) => (
          <div key={step.step} className="bg-background-100 rounded-xl p-5 border border-foreground-100/30">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{step.step}</div>
              <h3 className="font-heading font-semibold text-foreground-900 text-base">{step.title}</h3>
            </div>
            <p className="text-sm text-foreground-600 ml-11">{step.desc}</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Phase 3: Bestellung und Versand
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        Sobald du deine Produkte im <strong>OOPBUY Spreadsheet</strong> ausgewählt hast, geht es an die Bestellung. Das Spreadsheet hilft dir auch hier, den Überblick zu behalten und keine wichtigen Schritte zu vergessen.
      </p>

      <div className="space-y-4 my-6">
        {[
          { step: "7", title: "Warenkorb zusammenstellen", desc: "Füge alle gewünschten Produkte deinem Warenkorb hinzu. Das OOPBUY Spreadsheet zeigt dir die Gesamtkosten übersichtlich an." },
          { step: "8", title: "Versandmethode wählen", desc: "Wähle die passende Versandmethode aus. Berücksichtige dabei Lieferzeit und Kosten — beide Informationen findest du im Spreadsheet." },
          { step: "9", title: "Bestellung abschließen", desc: "Überprüfe alle Angaben ein letztes Mal und schließe deine Bestellung ab. Speichere die Bestellnummer für die spätere Nachverfolgung." },
        ].map((step) => (
          <div key={step.step} className="bg-background-100 rounded-xl p-5 border border-foreground-100/30">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{step.step}</div>
              <h3 className="font-heading font-semibold text-foreground-900 text-base">{step.title}</h3>
            </div>
            <p className="text-sm text-foreground-600 ml-11">{step.desc}</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        5 Pro-Tipps für effizienteres Einkaufen
      </h2>
      <ul className="space-y-3 list-none pl-0 text-foreground-700 my-6">
        {tips.map((tip, i) => (
          <li key={i} className="flex items-start gap-2">
            <i className="ri-star-line text-accent-500 w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
            <span><strong>Tipp {i + 1}:</strong> {tip}</span>
          </li>
        ))}
      </ul>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Häufige Fragen zum Einkaufsprozess
      </h2>
      <div className="space-y-4 my-6">
        {faqs.map((faq, i) => (
          <div key={i} className="bg-background-100 rounded-xl p-5 border border-foreground-100/30">
            <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{faq.q}</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 pt-8 border-t border-foreground-200">
        <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">Weiterführende Ressourcen</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-50 text-primary-700 rounded-full text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            Zur Startseite
          </Link>
          <Link to="/oopbuy-sneaker-spreadsheet-guide" className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-footprint-line w-4 h-4 flex items-center justify-center"></i>
            Sneaker Guide
          </Link>
          <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
            Zum OOPBUY Spreadsheet
          </a>
        </div>
      </div>
    </ArticleLayout>
  );
}